import React from "react";
import lodashDebounce from "lodash/debounce";
import { ColDef, ColGroupDef, GridApi, ValueFormatterParams, ValueGetterParams } from "ag-grid-community";
import { RowDataByTargetId } from "../financial-target.types";
import { getDoorTargetValue, isColGroup } from "../helpers";
import { TitleCellRenderer } from "../components/title-cell-renderer";
import { MappedFinancialTarget } from "components/product-collection/footer/types";
import { DisplayedGridRows } from "reducers/collection-target/types";
import { ProductCollectionGridView } from "../../product-collection-grid-view";
import { FINANCIAL_TARGET_ROW_IDS } from "../constants";
import { metricsNumberFormatter } from "helpers/metrics/formatters/metrics-number-formatter";
import { formatThousands } from "lib/number";
import { MetricsState } from "reducers/dimensions-panel/dimensions-panel";
import { Metric, MetricsKind } from "helpers/metrics/types";

// Default image column definition
const imageColumn = {
    field: "image",
    headerName: "Image",
    pinned: "left",
    suppressColumnsToolPanel: true,
    suppressMenu: true,
    cellStyle: {
        fontWeight: "bold",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
};

/**
 * Helper function to generate col defs
 * @param targets
 * @param filteredRowDataByTargetId
 * @param mainGridApi
 * @param gridViewRef
 * @param displayedRows
 * @param isCollapseModeOn
 * @param metrics
 * @returns
 */
const generateColDefs = (
    targets,
    filteredRowDataByTargetId,
    mainGridApi,
    gridViewRef,
    displayedRows,
    isCollapseModeOn: boolean,
    metrics: MetricsState
) => {
    // counters for the first column, which is the target's title bearing one
    let spanForFirstColumnCounter = 0;
    let imageColWidth: undefined | number = undefined;

    // Maps children columns of a ColGroup, this is where the target plan columns are created as well
    const mapColGroupChildren = (childrenColumns: ColDef[] | ColGroupDef[]) => {
        return childrenColumns.map((c) => {
            if (isColGroup(c)) {
                return {
                    headerName: c.headerName,
                    headerClass: c.headerClass,
                    suppressColumnsToolPanel: true,
                    openByDefault: c.openByDefault,
                    columnGroupShow: c.columnGroupShow,
                    suppressMenu: true,
                    groupId: c.groupId,
                    children: mapColGroupChildren(c.children),
                    field: c.headerName,
                };
            }

            return {
                colId: c.colId,
                cellClass: c.cellClass,
                cellStyle: c.cellStyle,
                headerName: c.headerName,
                headerClass: c.headerClass,
                width: c.width,
                columnGroupShow: c.columnGroupShow,
                cellRendererParams: c.cellRendererParams,
                type: c.type,
                valueGetter: (getterParams: ValueGetterParams) =>
                    getDoorTargetValue(
                        filteredRowDataByTargetId,
                        mainGridApi,
                        gridViewRef,
                        getterParams,
                        targets,
                        displayedRows,
                        isCollapseModeOn
                    ),
                valueFormatter: (params: ValueFormatterParams) => {
                    const {
                        data,
                        value,
                        colDef: { colId },
                    } = params;
                    if (data.rowType === FINANCIAL_TARGET_ROW_IDS.VARIANCE_PERCENTAGE) {
                        return value !== null && value !== undefined
                            ? `${formatThousands(Number(value).toFixed(1))}%`
                            : value;
                    }

                    const metricsKind: MetricsKind = data.targetMetricsType;
                    // Retrieve metric format from redux state
                    const metric: Metric = metrics.values.find((m) => m.kind === metricsKind);
                    if (!metric) {
                        return value;
                    }

                    let formattedValue = "";
                    const isTargetMetricColumn = colId?.includes(`|${metric.kind}|`);

                    // We need `isCollapseModeOn` because when that mode is on, it should override formatting
                    if (isTargetMetricColumn || isCollapseModeOn) {
                        formattedValue = metricsNumberFormatter({
                            kind: metric.kind,
                            numberFormat: metric.numberFormat,
                        })(params);
                    }

                    return formattedValue;
                },
            };
        });
    };

    // mapping the columns from the main grid, removing all unecessary props
    const mappedColumns = mainGridApi.getColumnDefs().reduce((acc, currentCol: ColDef | ColGroupDef) => {
        if (isColGroup(currentCol)) {
            const { headerName, headerClass, groupId, children, openByDefault, columnGroupShow } = currentCol;

            return [
                ...acc,
                {
                    headerName,
                    suppressColumnsToolPanel: true,
                    headerClass,
                    openByDefault,
                    columnGroupShow,
                    suppressMenu: true,
                    groupId,
                    children: mapColGroupChildren(children),
                    field: headerName,
                    cellStyle: {
                        borderLeft: "2px solid rgb(125, 142, 135)",
                        display: "flex",
                        justifyContent: "flex-end",
                    },
                },
            ];
        }

        const { field, colId, hide, width, headerName, headerClass, pinned, rowGroup } = currentCol;

        // counting how many spans we have to apply to the first column to
        // match the width of the main grid
        if (pinned === "left") {
            spanForFirstColumnCounter += 1;
        }

        // storing the width of the image col to use it in the image column
        // definition bellow
        if (colId === "image_url") {
            imageColWidth = width;
            return acc;
        }
        return [
            ...acc,
            {
                field,
                colId,
                headerName,
                width,
                suppressColumnsToolPanel: true,
                suppressMenu: true,
                headerClass,
                hide,
                pinned,
                rowGroup,
            },
        ];
    }, []);

    const imageCellRowSpan = ({ data }) => {
        if (data?.targetInfo) {
            const activeRowsLength = Object.keys(displayedRows).filter((k) => displayedRows[k]).length;
            return activeRowsLength;
        }
        return 1;
    };

    // We need the image column to always be the first one, so we create it here
    const imageCol: ColDef = {
        ...imageColumn,
        width: imageColWidth,
        cellClassRules: {
            "cell-span": ({ data }) =>
                displayedRows &&
                Object.values(displayedRows)?.filter((value) => value)?.length > 1 &&
                data.targetInfo?.schema,
            "show-cell": "value !== undefined",
            "cell-span-single-row": ({ data }) =>
                displayedRows &&
                Object.values(displayedRows)?.filter((value) => value)?.length === 1 &&
                data.targetInfo?.schema,
        },
        colSpan: () => spanForFirstColumnCounter,
        rowSpan: imageCellRowSpan,
        valueGetter: ({ data }) => data.targetInfo?.description,
        cellRenderer: ({ value, data }) => (
            <TitleCellRenderer value={value} displayedRows={displayedRows} targetInfo={data.targetInfo} />
        ),
    };

    const columns = [imageCol, ...mappedColumns];

    return columns;
};

const debouncedGenerateColDefs = lodashDebounce(
    (
        targets,
        filteredRowDataByTargetId,
        mainGridApi,
        gridViewRef,
        displayedRows,
        callback,
        isCollapseModeOn,
        metrics
    ) => {
        callback(
            generateColDefs(
                targets,
                filteredRowDataByTargetId,
                mainGridApi,
                gridViewRef,
                displayedRows,
                isCollapseModeOn,
                metrics
            )
        );
    },
    250
);

/**
 * Create column definitions for the financial target grid
 * @param filteredRowDataByTargetId row nodes that will be used in the value getter for the columns,
 * filtered based on the targets
 * @param mainGridApi the connected grid API
 * @param gridViewRef
 * @param targets targets list
 * @param displayedRows
 * @param isCollapseModeOn
 * @param metrics
 * @returns
 */
export const useColumns = (
    filteredRowDataByTargetId: RowDataByTargetId,
    mainGridApi: GridApi,
    gridViewRef: React.RefObject<ProductCollectionGridView>,
    targets: MappedFinancialTarget[],
    displayedRows: DisplayedGridRows,
    isCollapseModeOn: boolean,
    metrics: MetricsState
): ColDef[] => {
    const [columnDefs, setColumnDefs] = React.useState(
        generateColDefs(
            targets,
            filteredRowDataByTargetId,
            mainGridApi,
            gridViewRef,
            displayedRows,
            isCollapseModeOn,
            metrics
        )
    );

    React.useEffect(() => {
        debouncedGenerateColDefs(
            targets,
            filteredRowDataByTargetId,
            mainGridApi,
            gridViewRef,
            displayedRows,
            (colDefs) => {
                setColumnDefs(colDefs);
            },
            isCollapseModeOn,
            metrics
        );
    }, [
        targets,
        mainGridApi,
        filteredRowDataByTargetId,
        displayedRows,
        gridViewRef,
        setColumnDefs,
        isCollapseModeOn,
        metrics,
    ]);

    return columnDefs;
};
